'use client';

import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { classNames } from '../../utils/class-names';

import styles from './tooltip.module.css';

let tooltipRoot: HTMLElement | null;

if (typeof window !== 'undefined') {
  tooltipRoot = document.getElementById('tooltip-root');

  if (tooltipRoot === null) {
    tooltipRoot = document.createElement('div');
    tooltipRoot.id = 'tooltip-root';
    document.body.appendChild(tooltipRoot);
  }
}

export type TooltipPortalProps = {
  visible?: boolean;
  variant?: 'primary' | 'secondary';
  position?: 'above' | 'left' | 'right';
} & HTMLAttributes<HTMLDivElement>;

const TooltipPortal = forwardRef<HTMLDivElement, TooltipPortalProps>(function TooltipPortal(
  { visible, variant = 'primary', hidden, className, ...props },
  ref
) {
  if (!tooltipRoot) return null;

  return createPortal(
    <div
      ref={ref}
      className={classNames(
        styles.tooltip,
        visible && styles.visible,
        variant && styles[variant],
        hidden && styles.hidden,
        className
      )}
      {...props}
    />,
    tooltipRoot
  );
});

export type TooltipProps = {
  label: ReactNode;
  delay?: number;
  alwaysVisible?: boolean;
} & TooltipPortalProps;

/**
 * @param {ReactNode} label The content of the tooltip
 * @default undefined
 * @param {number} [delay] The delay in milliseconds before the tooltip is shown
 * @default 700
 * @param {boolean} [alwaysVisible] Whether the tooltip should remain visible
 * @default false
 * @param {boolean} [hidden] Whether the tooltip should remain hidden
 * @default false
 * @param {'above' | 'left' | 'right'} [position] A fixed position of the tooltip relative to the element that triggers it
 * @default undefined
 */
export function Tooltip({ label, delay = 700, alwaysVisible = false, position, children, ...props }: TooltipProps) {
  const childrenWrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({ left: 0, top: 0 });
  const [className, setClassName] = useState(['']);
  const [isMounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const timeout = useRef(0);

  const calculatePosition = () => {
    const elementNode = childrenWrapperRef.current?.firstElementChild;
    const tooltipNode = tooltipRef.current;

    if (!elementNode || !tooltipNode) return;

    const margin = 8;
    const element = elementNode.getBoundingClientRect();
    const tooltip = tooltipNode.getBoundingClientRect();

    if (position === 'left') {
      const left = element.left - tooltip.width - margin;
      const top = element.top + element.height / 2 - tooltip.height / 2;

      setStyle({ left, top });
      setClassName([styles.center, styles.left]);
      return;
    }

    if (position === 'right') {
      const left = element.left + element.width + margin;
      const top = element.top + element.height / 2 - tooltip.height / 2;

      setStyle({ left, top });
      setClassName([styles.center, styles.right]);
      return;
    }

    if (position === 'above') {
      const left = element.left + element.width / 2 - tooltip.width / 2;
      const top = element.top - tooltip.height - margin;

      setStyle({ left, top });
      setClassName([styles.above]);
      return;
    }

    const className = [''];
    let left = element.left + element.width / 2 - tooltip.width / 2;
    let top = element.top + element.height + margin;

    const isOutsideBottom = top + tooltip.height > window.innerHeight;
    const isOutsideLeft = left < 0;
    const isOutsideRight = left + tooltip.width > window.innerWidth;

    if (isOutsideBottom) {
      top = top - element.height - tooltip.height - margin * 2;
      className.push(styles.above);
    }

    if (isOutsideLeft) {
      left = element.left;
      className.push(styles.left);
    }

    if (isOutsideRight) {
      left = element.left - tooltip.width + element.width;
      className.push(styles.right);
    }

    setStyle({ left, top });
    setClassName(className);
  };

  const onMouseEnter = () => {
    clearTimeout(timeout.current);

    timeout.current = window.setTimeout(() => {
      calculatePosition();
      setVisible(true);
    }, delay);
  };

  const onMouseLeave = () => {
    clearTimeout(timeout.current);
    setVisible(false);
  };

  useEffect(calculatePosition, [position, isMounted]);
  useEffect(() => {
    setMounted(true);
    return () => clearTimeout(timeout.current);
  }, []);

  return (
    <div
      ref={childrenWrapperRef}
      className={styles.childrenWrapper}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isMounted && (
        <TooltipPortal
          ref={tooltipRef}
          {...props}
          className={classNames(className.join(' '), props?.className)}
          style={style}
          visible={alwaysVisible || visible}
        >
          {label}
        </TooltipPortal>
      )}

      {children}
    </div>
  );
}
